/**
 * Two ways to get the paperwork signed.
 *
 * In person is offered first and described as the stronger one, because it is:
 * Florida wants identity proofing at NIST assurance level 2 before the title
 * paperwork may be signed electronically, and a person checking a licence
 * across a desk is exactly that. A code read over the telephone is not, and
 * the wording here does not pretend otherwise — a dealer choosing the
 * convenient option should know what they are trading away.
 *
 * This panel also has to say what has come back. It used to show "waiting" for
 * any unfinished session, which reads the same whether the customer has not
 * opened the link or has signed, closed her phone and is waiting on the
 * dealership. On 1 October 2026 that is exactly what happened: a signature was
 * sitting in the database and the deal screen said "waiting". A panel that
 * cannot tell those two apart is worse than no panel, because it is trusted.
 */

import { useCallback, useEffect, useState } from "react";
import { Button, ErrorNote, Panel } from "../../components/ui";
import { EmailField } from "../../components/fields";
import {
  sendSigningLink, listSessions, pendingProgress,
  type SigningSession, type StoredSignature,
} from "./signing";
import { ROLE_LABEL } from "../../lib/signing/anchors";
import { usDate } from "../../forms/mappings";

export default function SignChoice({
  dealId, dealNo, docKeys, buyerName, coBuyerName, buyerEmail, vehicle, dealerName,
  onInPerson, onFinish,
}: {
  dealId: string;
  dealNo: string;
  docKeys: string[];
  buyerName: string;
  coBuyerName: string | null;
  buyerEmail: string | null;
  vehicle: string;
  dealerName: string;
  onInPerson: () => void;
  /** Carry on with a session somebody has already signed. */
  onFinish: (session: SigningSession) => void;
}) {
  const [sessions, setSessions] = useState<SigningSession[]>([]);
  const [arrived, setArrived] = useState<StoredSignature[]>([]);
  const [sending, setSending] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [email, setEmail] = useState(buyerEmail ?? "");
  const [issued, setIssued] = useState<{ code: string; url: string; expiresAt: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);

  /* The person, not the role. "Buyer has signed — Buyer · 11:58" says the same
     word twice and neither time is it a name. */
  const nameFor = (r: StoredSignature["role"]) =>
    r === "buyer" ? buyerName
    : r === "co_buyer" ? (coBuyerName ?? ROLE_LABEL.co_buyer)
    : dealerName;

  const load = useCallback(() => {
    listSessions(dealId).then(setSessions).catch((e: Error) => setErr(e.message));
    pendingProgress(dealId)
      .then((p) => { setArrived(p?.signatures ?? []); setCheckedAt(new Date()); })
      .catch(() => { /* the session list already carries the error */ });
  }, [dealId]);

  useEffect(load, [load]);

  const signed = sessions.find((s) => s.status === "completed");
  const pending = sessions.find((s) => s.status === "sent" || s.status === "in_progress");

  /* While a link is outstanding, keep looking. A signature arrives from the
     customer's phone, so nothing in this tab knows about it: without this the
     dealer has to guess when to reload, which is the same as not being told.
     Also on regaining focus, which covers the dealer who left the tab open and
     came back after the phone call. */
  useEffect(() => {
    if (!pending || signed) return;
    const tick = () => load();
    const timer = window.setInterval(tick, 20_000);
    window.addEventListener("focus", tick);
    return () => { window.clearInterval(timer); window.removeEventListener("focus", tick); };
  }, [pending, signed, load]);

  const send = async () => {
    if (!email.trim()) { setErr("Where should the link go?"); return; }
    setSending(true); setErr(null);
    try {
      const sent = await sendSigningLink(dealId, docKeys, {
        email: email.trim(), name: buyerName, vehicle, dealNo, dealerName,
      });
      setIssued({ code: sent.code, url: sent.url, expiresAt: sent.expiresAt });
      load();
    } catch (e) { setErr((e as Error).message); }
    finally { setSending(false); }
  };

  return (
    <Panel title="Signatures" rail="keep"
           figure={signed ? "signed"
                   : arrived.length ? "to finish"
                   : pending ? "waiting" : undefined}>
      {err && <ErrorNote>{err}</ErrorNote>}

      {signed ? (
        <p className="hint">
          This packet was signed electronically on {usDate(signed.completed_at!.slice(0, 10))}.
          The signed copy and its certificate are filed below.
        </p>
      ) : (
        <>
          {/* Somebody has signed and the packet is not finished. This outranks
              everything else on the panel: it is the one state with a customer
              on the other end of it, already signed and holding nothing. */}
          {pending && arrived.length > 0 && (
            <div className="arrivedcard">
              <div className="sentcard-top">
                <span className="tick-big" aria-hidden="true">✓</span>
                <div>
                  <strong>
                    {arrived.length === 1
                      ? `${nameFor(arrived[0]!.role)} has signed`
                      : `${arrived.length} signatures received`}
                  </strong>
                  <span className="hint">
                    {arrived.map((x) =>
                      (arrived.length === 1 ? "" : `${nameFor(x.role)} · `)
                      + new Date(x.signed_at).toLocaleString()).join("  ·  ")}
                    {pending?.mode === "remote" ? " · signed from the link" : ""}
                  </span>
                </div>
              </div>
              <p className="taxnote">
                Nothing has been stamped onto the documents yet. Add the
                dealership signature to finish: that applies every signature
                across all {docKeys.length} documents, attaches the certificate
                of electronic signature, files the hashed copy and lets you email
                it to {buyerName.split(" ")[0]}.
              </p>
              <div className="actions">
                <Button onClick={() => onFinish(pending)}>
                  Add the dealership signature and finish
                </Button>
              </div>
            </div>
          )}

          <p className="hint">
            Florida accepts electronic signatures on the title paperwork, but
            only where the signer's identity has been properly established.
            Signing at the desk, against their licence, is the version that
            holds up.
          </p>

          {arrived.length === 0 && (
            <div className="actions">
              <Button onClick={onInPerson}>Sign here, on a tablet</Button>
              {!showLink && (
                <Button variant="ghost" onClick={() => setShowLink(true)}>
                  Send a link instead
                </Button>
              )}
            </div>
          )}

          {showLink && !issued && (
            <div className="drafter">
              <p className="taxnote">
                Weaker, and worth knowing why: a link proves the person has the
                email address and the phone number you already had for them. It
                does not prove who they are. Use it when the customer cannot
                come in, and expect a tax collector to look harder at the
                certificate.
              </p>
              <EmailField label="Send the link to" value={email} onChange={setEmail} required />
              <div className="actions">
                <Button disabled={sending} onClick={send}>
                  {sending ? "Sending…" : "Send the link"}
                </Button>
                <Button variant="ghost" onClick={() => setShowLink(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {issued && (
            <div className="codecard">
              <p className="hint">
                Link sent to {email}. Now phone {buyerName.split(" ")[0]} and read
                them this code. It is shown once and is not stored anywhere.
              </p>
              <div className="bigcode mono">{issued.code}</div>
              <p className="taxnote">
                Expires {new Date(issued.expiresAt).toLocaleString()}. Do not
                email or text the code — sending it down the same wire as the
                link is the same as having no code at all.
              </p>
            </div>
          )}

          {pending && arrived.length === 0 && !issued && (
            <p className="taxnote">
              A signing link is outstanding
              {pending.expires_at
                ? `, good until ${new Date(pending.expires_at).toLocaleString()}` : ""}
              {" "}and nobody has signed it yet. This panel checks for itself every
              twenty seconds, so leave it open — you will see the signature here
              when it arrives, and you will get an email.
              {checkedAt && (
                <span className="hint">
                  {" "}Last checked {checkedAt.toLocaleTimeString([], {
                    hour: "numeric", minute: "2-digit", second: "2-digit" })}.
                </span>
              )}
            </p>
          )}
        </>
      )}
    </Panel>
  );
}
